import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api.ts';
import CategorySidebar from '../CategorySidebar.tsx';
import ConfirmModal from '../ConfirmModal.tsx';
import ManageCategoriesModal from '../ManageCategoriesModal.tsx';
import MoveToCategoryModal from '../MoveToCategoryModal.tsx';
import Toast from '../Toast.tsx';
import BankFacts from './BankFacts.tsx';
import BankToolbar from './BankToolbar.tsx';
import { AllAssets, FileCategory, SearchResults } from './BankViews.tsx';
import { makeSetItemStatus, useBank } from './useBank.ts';
import {
  BUILTIN_CATEGORIES,
  BUILTIN_META,
  FALLBACK_CATEGORY,
  bankCategoryTree,
  isArchived,
  isUnder,
  labelFor,
  leafOf,
  matchesQuery,
  slugifyPath,
  type BankActions,
  type BankConfirm,
  type BankStatusFilter,
} from './bankShared.ts';
import type { CategoryTreeNode } from '../../lib/categoryTree.ts';
import type { BankItem } from '../../../shared/deck.ts';

/**
 * The Library page's Bank tab (`/library/bank`): upload, edit, categorise,
 * archive. This component IS that page's surface, which is why the `?category=`
 * sync lives here rather than in the shared entry — the Assets drawer's picker
 * must never touch the URL.
 */
/**
 * `onCount` reports how many assets the bank holds, for the Library's page
 * title. It is a callback rather than the title reading the bank itself
 * because `useBank` fetches on mount: a second copy in the route would pay
 * for GET /api/bank on every Slides visit, for a subtitle.
 */
export default function BankLibrary({
  onCount,
  query,
  onQuery,
}: {
  onCount?: (n: number) => void;
  /** The bank's free-text filter. Owned by the route, because the field that
   *  drives it renders in the page title beside the heading, not in here. */
  query: string;
  onQuery: (q: string) => void;
}) {
  const { state, reload, busy, run, toast, pushToast, runUndo, dismissToast } = useBank();
  const [cat, setCat] = useState<string | null>(null); // null = the "All assets" overview
  const setQuery = onQuery;
  const [statusFilter, setStatusFilter] = useState<BankStatusFilter>('active');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [manageCats, setManageCats] = useState(false);
  const [confirm, setConfirm] = useState<BankConfirm>(null);
  const [moveItem, setMoveItem] = useState<BankItem | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const hydratedFromUrl = useRef(false);

  // Deep-links: hydrate the selected category from the URL once (?category=),
  // then mirror it back so a category link is copy-pasteable. Same pattern as
  // the Dashboard and the Library's Slides tab.
  useEffect(() => {
    if (hydratedFromUrl.current) return;
    hydratedFromUrl.current = true;
    const fromUrl = searchParams.get('category');
    if (fromUrl) setCat(fromUrl);
  }, [searchParams]);
  useEffect(() => {
    if (!hydratedFromUrl.current) return;
    const next = new URLSearchParams(searchParams);
    if (cat === null) next.delete('category');
    else next.set('category', cat);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat]);

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;

  // Sidebar tabs: every server category (built-ins pinned, customs in registry
  // order, facts last).
  const allCats = state.categories;

  const inStatus = useCallback(
    (it: BankItem) => (statusFilter === 'all' ? true : statusFilter === 'archived' ? isArchived(it) : !isArchived(it)),
    [statusFilter],
  );

  // Two-level sidebar tree (bankShared.ts — the picker rail builds the same
  // one, which is why it moved there). Counts honour the status filter here: a
  // count that includes rows the grid won't show reads as a bug.
  const catTree = useMemo<CategoryTreeNode[]>(
    () => bankCategoryTree(allCats, state.items, inStatus),
    [allCats, state.items, inStatus],
  );

  // "All assets" (cat === null) aggregates every image asset; facts are text,
  // so they stay their own row and are excluded from the All view.
  const imageItems = state.items.filter((it) => it.category !== 'facts' && inStatus(it));

  // Status counts are taken across the whole bank, not the active view — the
  // chips are how you FIND the archived items, so their count can't depend on
  // a filter you can only reach by clicking them.
  const archivedCount = state.items.filter(isArchived).length;
  const counts = { active: state.items.length - archivedCount, archived: archivedCount, all: state.items.length };

  const total = state.status === 'ready' ? state.items.length : null;
  useEffect(() => {
    if (total !== null) onCount?.(total);
  }, [total, onCount]);

  // Tag chips come from what is actually visible under the current category +
  // status, so a tag can never show a zero count.
  const tags = useMemo(() => {
    const scope = state.items.filter((it) => inStatus(it) && (searching || cat === null || isUnder(it.category, cat)));
    return [...new Set(scope.flatMap((it) => it.tags || []))].sort();
  }, [state.items, inStatus, searching, cat]);
  // A tag that has just disappeared from view must not keep filtering silently.
  useEffect(() => {
    if (tagFilter && !tags.includes(tagFilter)) setTagFilter(null);
  }, [tags, tagFilter]);

  const byTag = (it: BankItem) => !tagFilter || (it.tags || []).includes(tagFilter);

  // A query searches the WHOLE bank and supersedes the category selection —
  // the same behaviour as the media picker's bank shelf. Finding an asset you
  // can't remember filing is the entire point.
  const searchResults = searching ? state.items.filter((it) => inStatus(it) && byTag(it) && matchesQuery(it, q)) : [];
  const activeItems = state.items.filter((it) => inStatus(it) && byTag(it) && cat && isUnder(it.category, cat));
  const activeMeta =
    cat === null || cat === 'facts'
      ? null
      : BUILTIN_META[cat] || {
          label: labelFor(leafOf(cat)),
          hint: `Drop an image here or click to upload to ${labelFor(leafOf(cat))}`,
        };

  // ---------------------------------------------------------------------
  // Category management

  async function onCreateCategory(path: string) {
    await run(async () => {
      await api.post('/api/bank/categories/create', { path: slugifyPath(path) });
      await reload();
    }, 'Creating category failed');
  }

  async function onRenameCategory(from: string, to: string) {
    await run(async () => {
      const next = slugifyPath(to);
      await api.put('/api/bank/categories/rename', { from, to: next });
      await reload();
      // Following the rename keeps the user where they were looking.
      setCat((c) => (c === from ? next : c && c.startsWith(from + '/') ? next + c.slice(from.length) : c));
      pushToast(`Renamed to ${labelFor(leafOf(next))}`);
    }, 'Renaming category failed');
  }

  async function onReorderCategories(order: string[]) {
    await run(async () => {
      await api.put('/api/bank/categories/order', { order });
      await reload();
    }, 'Reordering categories failed');
  }

  function onDeleteCategory(path: string) {
    const count = state.items.filter((it) => isUnder(it.category, path)).length;
    const done = async (moveTo: string | null) => {
      await run(async () => {
        await api.post('/api/bank/categories/delete', { path, ...(moveTo ? { moveTo } : {}) });
        await reload();
        if (cat && isUnder(cat, path)) setCat(null);
        pushToast(
          moveTo
            ? `Category deleted, ${count} asset${count === 1 ? '' : 's'} moved to ${labelFor(moveTo)}`
            : 'Category deleted',
        );
      }, 'Deleting category failed');
      setConfirm(null);
    };
    if (count === 0) {
      setConfirm({
        title: `Delete the "${labelFor(leafOf(path))}" category?`,
        body: 'It is empty, so nothing else changes.',
        danger: true,
        confirmLabel: 'Delete category',
        onConfirm: () => done(null),
      });
      return;
    }
    // Deleting a non-empty category is a genuine fork, so both outcomes are
    // buttons rather than a second dialog: bin the assets, or keep them.
    setConfirm({
      title: `Delete the "${labelFor(leafOf(path))}" category?`,
      body: (
        <>
          <p>
            It holds {count} asset{count === 1 ? '' : 's'} (including any sub-categories). Deleting the assets removes
            their files for good: decks that already use them keep their copy.
          </p>
          <p className="muted bank__note">Moving them instead files them under {labelFor(FALLBACK_CATEGORY)}.</p>
        </>
      ),
      danger: true,
      confirmLabel: `Delete category and ${count} asset${count === 1 ? '' : 's'}`,
      secondaryLabel: `Move ${count === 1 ? 'it' : 'them'} to ${labelFor(FALLBACK_CATEGORY)}`,
      onSecondary: () => done(FALLBACK_CATEGORY),
      onConfirm: () => done(null),
    });
  }

  async function saveItemCategory(item: BankItem, category: string) {
    await run(async () => {
      await api.put(`/api/bank/items/${item.id}`, { category });
      await reload();
      pushToast(`Moved to ${labelFor(leafOf(category))}`);
    }, 'Move failed');
    setMoveItem(null);
  }

  const actions: BankActions = {
    run,
    reload,
    pushToast,
    setConfirm,
    setMoveItem,
    setItemStatus: makeSetItemStatus({ run, reload, pushToast }),
  };

  if (state.status === 'loading') return <div className="muted bank__empty">Loading bank…</div>;
  if (state.status === 'error') {
    return (
      <div className="error">
        <strong>Couldn't load the RKH Bank</strong>
        <p className="muted bank__error">{state.error}</p>
      </div>
    );
  }

  return (
    <div className="bank bank--manage">
      <BankToolbar
        statusFilter={statusFilter}
        onStatusFilter={setStatusFilter}
        counts={counts}
        tags={tags}
        tagFilter={tagFilter}
        onTagFilter={setTagFilter}
      />

      {/* Same sidebar-rail chrome as the decks dashboard + Library Slides mode. */}
      <div className="dashboard__layout">
        <CategorySidebar
          tree={catTree}
          active={searching ? null : cat}
          allCount={imageItems.length}
          uncategorisedCount={0}
          uncategorisedKey="__none"
          onSelect={(next) => {
            setCat(next);
            setQuery('');
          }}
          onManage={() => setManageCats(true)}
          ariaLabel="Bank categories"
          allLabel="All assets"
        />

        <div className="dashboard__main">
          {searching ? (
            <SearchResults items={searchResults} query={query} busy={busy} actions={actions} />
          ) : cat === null ? (
            <AllAssets
              categories={allCats}
              items={imageItems}
              statusFilter={statusFilter}
              busy={busy}
              actions={actions}
            />
          ) : cat === 'facts' ? (
            <BankFacts items={activeItems} busy={busy} actions={actions} />
          ) : (
            <FileCategory category={{ key: cat, ...activeMeta }} items={activeItems} busy={busy} actions={actions} />
          )}
        </div>
      </div>

      <Toast toast={toast} onUndo={runUndo} onDismiss={dismissToast} />
      {confirm && <ConfirmModal {...confirm} busy={busy} onCancel={() => setConfirm(null)} />}
      {manageCats && (
        <ManageCategoriesModal
          tree={catTree}
          busy={busy}
          lockedPaths={BUILTIN_CATEGORIES}
          onClose={() => setManageCats(false)}
          onCreate={onCreateCategory}
          onRename={onRenameCategory}
          onDelete={onDeleteCategory}
          onReorder={onReorderCategories}
        />
      )}
      {moveItem && (
        <MoveToCategoryModal
          deck={{ category: moveItem.category, title: moveItem.label }}
          allCategories={allCats.filter((c) => c !== 'facts')}
          busy={busy}
          moveLabel="Move asset"
          clearLabel={`Reset to ${labelFor(FALLBACK_CATEGORY)}`}
          onCancel={() => setMoveItem(null)}
          // The modal is in SINGLE mode (no `multi`), so it only ever sends a
          // path or null; the array arm of its callback belongs to the decks
          // dashboard. A non-string reads as "cleared", which is what null
          // already meant here.
          onSave={(category) =>
            saveItemCategory(moveItem, slugifyPath(typeof category === 'string' ? category : null) || FALLBACK_CATEGORY)
          }
        />
      )}
    </div>
  );
}
